// src/utils/fileUtils.js

import { compressImage } from './imageUtils';

/**
 * Convert any file to base64
 * @param {File} file - The file object from input
 * @returns {Promise<string>} - Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * Process a file for upload, handling different types appropriately
 * @param {File} file - The file to process
 * @returns {Promise<object>} - Processed file data
 */
export const processFile = async (file) => {
  // Get base file information
  const fileInfo = {
    originalName: file.name,
    fileType: file.type,
    size: file.size,
  };

  // Process file based on type
  if (file.type.startsWith('image/')) {
    // For images, use existing image compression
    const processedImage = await compressImage(file);
    return {
      ...processedImage,
      fileType: file.type,
      id: Date.now() + Math.random()
    };
  } else {
    // For other files, just convert to base64
    const base64 = await fileToBase64(file);
    return {
      ...fileInfo,
      base64,
      id: Date.now() + Math.random()
    };
  }
};

/**
 * Get appropriate icon and display info for different file types
 * @param {string} fileType - MIME type of the file
 * @returns {object} - Icon and display information
 */
export const getFileTypeInfo = (fileType) => {
  // Default file icon and info
  let icon = 'file-text';
  let color = 'gray';
  let displayType = 'File';
  let canPreview = false;

  // Determine file type specifics
  if (fileType.startsWith('image/')) {
    icon = 'image';
    color = 'blue';
    displayType = 'Image';
    canPreview = true;
  } else if (fileType === 'application/pdf') {
    icon = 'file-text';
    color = 'red';
    displayType = 'PDF';
    canPreview = true;
  } else if (fileType.includes('word') || fileType === 'application/msword' || 
             fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    icon = 'file-text';
    color = 'blue';
    displayType = 'Word Document';
  } else if (fileType.includes('excel') || fileType === 'application/vnd.ms-excel' || 
             fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    icon = 'file-spreadsheet';
    color = 'green';
    displayType = 'Excel Spreadsheet';
  } else if (fileType === 'application/zip' || fileType === 'application/x-zip-compressed') {
    icon = 'archive';
    color = 'amber';
    displayType = 'ZIP Archive';
  } else if (fileType === 'text/plain') {
    icon = 'file';
    color = 'gray';
    displayType = 'Text Document';
    canPreview = true;
  }

  return { icon, color, displayType, canPreview };
};

/**
 * Validate file size and type
 * @param {File} file - File to validate
 * @param {array} allowedTypes - Array of allowed MIME types or MIME type prefixes
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {object} - Validation result
 */
export const validateFile = (file, allowedTypes = null, maxSizeMB = 10) => {
  const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes
  
  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: `File is too large. Maximum size is ${maxSizeMB}MB.` 
    };
  }
  
  if (allowedTypes && allowedTypes.length > 0) {
    const isAllowed = allowedTypes.some(type => {
      // Support for wildcard types like "image/*"
      if (type.endsWith('/*')) {
        const prefix = type.split('/')[0];
        return file.type.startsWith(`${prefix}/`);
      }
      return file.type === type;
    });
    
    if (!isAllowed) {
      return { 
        valid: false, 
        error: 'File type not supported.' 
      };
    }
  }
  
  return { valid: true };
};